/**
 * Extracts the user's IP address from a Remix request
 * Handles various proxy headers that might be present
 */
export function getUserIp(request: Request): string | null {
    // Get the IP from various headers that proxies might set
    const forwardedFor = request.headers.get('x-forwarded-for');
    const realIp = request.headers.get('x-real-ip');
    const cfConnectingIp = request.headers.get('cf-connecting-ip'); // Cloudflare
    const xClientIp = request.headers.get('x-client-ip');
    const xForwarded = request.headers.get('x-forwarded');
    const forwarded = request.headers.get('forwarded');

    // Priority order: Cloudflare > X-Forwarded-For > X-Real-IP > others
    if (cfConnectingIp) {
        return cfConnectingIp;
    }

    if (forwardedFor) {
        // X-Forwarded-For can contain multiple IPs, take the first one
        const ip = forwardedFor.split(',')[0].trim();
        return ip;
    }

    if (realIp) {
        console.log('✅ Using x-real-ip:', realIp);
        return realIp;
    }

    if (xClientIp) {
        console.log('✅ Using x-client-ip:', xClientIp);
        return xClientIp;
    }

    if (xForwarded) {
        console.log('✅ Using x-forwarded:', xForwarded);
        return xForwarded;
    }

    if (forwarded) {
        // Parse Forwarded header (RFC 7239)
        const match = forwarded.match(/for=([^;,\s]+)/);
        if (match) {
            const ip = match[1].replace(/"/g, ''); // Remove quotes if present
            console.log('✅ Using forwarded:', ip);
            return ip;
        }
    }

    return null;
}

/**
 * Fetches the real public IP address from an external service
 * This is useful for local development when proxy headers aren't available
 */
export async function getPublicIp(): Promise<string | null> {
    try {
        // Try multiple services for reliability
        const services = [
            'https://api.ipify.org?format=json',
            'https://ipapi.co/json/',
            'https://api.myip.com',
            'https://ipinfo.io/json'
        ];

        for (const service of services) {
            try {
                const response = await fetch(service, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    const ip = data.ip || data.query || data.ipAddress;
                    if (ip) {
                        return ip;
                    }
                }
            } catch (error) {
                continue;
            }
        }

        return null;
    } catch (error) {
        console.error('Error fetching public IP:', error);
        return null;
    }
}

function isPrivateOrLocalIp(ip: string): boolean {
    const value = ip.trim().toLowerCase();
    if (!value || value === "unknown" || value === "::1" || value === "localhost") {
        return true;
    }
    if (value.startsWith("127.") || value.startsWith("10.") || value.startsWith("192.168.")) {
        return true;
    }
    // 172.16.0.0 – 172.31.255.255
    const match = value.match(/^172\.(\d+)\./);
    if (match) {
        const second = Number(match[1]);
        if (second >= 16 && second <= 31) return true;
    }
    // IPv6 unique-local / link-local
    if (value.startsWith("fc") || value.startsWith("fd") || value.startsWith("fe80:")) {
        return true;
    }
    return false;
}

/**
 * Enhanced IP detection that falls back to public IP service for local development
 */
export async function getUserIpEnhanced(request: Request): Promise<string | null> {
    // First try to get IP from headers
    const headerIp = getUserIp(request);

    if (headerIp && !isPrivateOrLocalIp(headerIp)) {
        return headerIp;
    }
    const publicIp = await getPublicIp();
    return publicIp;
}
